import { useEffect, useRef, useState } from "react";

/**
 * Progressively reveals a growing string so that bursty streaming chunks
 * render with a steady, animated typewriter feel instead of jumping.
 *
 * Design notes:
 *  - Initial render shows the full target text (no animation on history load).
 *  - Only growth from a previously shown prefix is animated. If the target
 *    shrinks or diverges, we snap to it.
 *  - The reveal rate is adaptive: if the backlog grows large (chunky burst),
 *    we speed up so we never fall further than `maxLagMs` behind.
 *  - Respects `prefers-reduced-motion`.
 */
interface SmoothTextOptions {
  charsPerSecond?: number;
  maxLagMs?: number;
}

interface SmoothTextResult {
  text: string;
  isAnimating: boolean;
}

const DEFAULT_CHARS_PER_SECOND = 90;
const DEFAULT_MAX_LAG_MS = 500;

const hasReducedMotionPreference = (): boolean => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

export const useSmoothText = (
  target: string,
  options?: SmoothTextOptions,
): SmoothTextResult => {
  const [displayed, setDisplayed] = useState(target);
  const displayedLenRef = useRef(target.length);
  const targetRef = useRef(target);
  const rafRef = useRef<number | null>(null);
  const lastTickRef = useRef(0);

  targetRef.current = target;

  const charsPerSecond = options?.charsPerSecond ?? DEFAULT_CHARS_PER_SECOND;
  const maxLagSec = (options?.maxLagMs ?? DEFAULT_MAX_LAG_MS) / 1000;

  useEffect(() => {
    if (hasReducedMotionPreference()) {
      displayedLenRef.current = target.length;
      setDisplayed(target);
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    // Snap if the target shrank or diverged from the currently displayed prefix
    // (e.g., a session switch replaced the buffer). This keeps us consistent
    // without ever showing stale characters.
    if (target.length < displayedLenRef.current || !target.startsWith(displayed)) {
      displayedLenRef.current = target.length;
      setDisplayed(target);
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    if (displayedLenRef.current >= target.length) return;
    if (rafRef.current != null) return;

    const tick = (now: number) => {
      rafRef.current = null;
      const dt = lastTickRef.current
        ? Math.min(0.1, (now - lastTickRef.current) / 1000)
        : 1 / 60;
      lastTickRef.current = now;

      const current = targetRef.current;
      const backlog = current.length - displayedLenRef.current;
      if (backlog <= 0) {
        lastTickRef.current = 0;
        setDisplayed(current);
        return;
      }

      // Adaptive reveal rate: never let the visible text fall more than
      // `maxLagSec` seconds behind the received stream.
      const adaptiveCps = Math.max(charsPerSecond, backlog / maxLagSec);
      const add = Math.max(1, Math.floor(adaptiveCps * dt));
      const nextLen = Math.min(current.length, displayedLenRef.current + add);
      displayedLenRef.current = nextLen;
      setDisplayed(current.slice(0, nextLen));

      if (nextLen < current.length) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        lastTickRef.current = 0;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    // `displayed` intentionally omitted: the running RAF loop reads from
    // `targetRef` and updates `displayedLenRef`, so every frame would
    // otherwise trigger a no-op effect re-run.
  }, [target, charsPerSecond, maxLagSec]);

  useEffect(() => {
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  return {
    text: displayed,
    isAnimating: displayed.length < target.length,
  };
};
